import {
  type RefObject,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { VerificationStageStatus } from "@/features/operations/verifications/types.ts";
import type { VerificationStage } from "../../types.ts";
import { sortAttachments } from "./constants.ts";

interface UseReviewConsoleParams {
  visible: boolean;
  order: VerificationStage | null;
  initialAttachmentIndex: number;
  onAccept: (order: VerificationStage) => Promise<void>;
  onReject: (order: VerificationStage, comment: string) => Promise<void>;
  isSubmitting: boolean;
}

export interface UseReviewConsoleReturn {
  // Attachments
  attachments: ReturnType<typeof sortAttachments>;
  currentAttachment: ReturnType<typeof sortAttachments>[number] | undefined;
  attIdx: number;
  setAttIdx: (idx: number) => void;
  goNextAttachment: () => void;
  goPrevAttachment: () => void;
  mediaLoading: boolean;
  setMediaLoading: (v: boolean) => void;

  // Actions
  canAccept: boolean;
  canReject: boolean;
  noActions: boolean;
  handleAccept: () => Promise<void>;

  // Reject mode
  rejectMode: boolean;
  rejectComment: string;
  rejectError: string;
  setRejectComment: (v: string) => void;
  setRejectError: (v: string) => void;
  enterRejectMode: () => void;
  cancelRejectMode: () => void;
  handleRejectConfirm: () => Promise<void>;
  textareaRef: RefObject<HTMLTextAreaElement>;
}

export const useReviewConsole = ({
  visible,
  order,
  initialAttachmentIndex,
  onAccept,
  onReject,
  isSubmitting,
}: UseReviewConsoleParams): UseReviewConsoleReturn => {
  // Lazy-initialize from props. Parent passes a fresh `key` on each new click,
  // so the initializers re-run on remount with up-to-date values.
  const [attIdx, setAttIdx] = useState(initialAttachmentIndex);
  const [rejectMode, setRejectMode] = useState(false);
  const [rejectComment, setRejectComment] = useState("");
  const [rejectError, setRejectError] = useState("");
  const [mediaLoading, setMediaLoading] = useState(true);
  const textareaRef = useRef<HTMLTextAreaElement>(null);

  const attachments = useMemo(() => sortAttachments(order), [order]);
  const currentAttachment = attachments[attIdx];

  // Per-row action gating based on this order's status.
  const status = order?.verificationStageStatus;
  const canAccept =
    !!order &&
    (status === VerificationStageStatus.WaitingVerification ||
      status === VerificationStageStatus.Rejected);
  const canReject =
    !!order && status === VerificationStageStatus.WaitingVerification;
  const noActions = !!order && !canAccept && !canReject;

  // Lock body scroll while open
  useEffect(() => {
    if (visible) {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "";
    }
    return () => {
      document.body.style.overflow = "";
    };
  }, [visible]);

  // Auto-focus textarea when reject mode opens
  useEffect(() => {
    if (rejectMode) textareaRef.current?.focus();
  }, [rejectMode]);

  const goNextAttachment = useCallback(() => {
    if (attachments.length <= 1) return;
    setMediaLoading(true);
    setAttIdx((i) => (i + 1) % attachments.length);
  }, [attachments.length]);

  const goPrevAttachment = useCallback(() => {
    if (attachments.length <= 1) return;
    setMediaLoading(true);
    setAttIdx((i) => (i - 1 + attachments.length) % attachments.length);
  }, [attachments.length]);

  const resetRejectState = useCallback(() => {
    setRejectMode(false);
    setRejectComment("");
    setRejectError("");
  }, []);

  const handleAccept = useCallback(async () => {
    if (!order || !canAccept || isSubmitting) return;
    try {
      await onAccept(order);
      // Parent decides what happens next (typically closes the modal).
    } catch {
      // toasts handled by parent; keep state intact for retry
    }
  }, [order, canAccept, isSubmitting, onAccept]);

  const handleRejectConfirm = useCallback(async () => {
    if (!order || !canReject || isSubmitting) return;
    const trimmed = rejectComment.trim();
    if (!trimmed) {
      setRejectError("الرجاء كتابة سبب الرفض");
      return;
    }
    try {
      await onReject(order, trimmed);
      resetRejectState();
    } catch {
      // keep reject mode open so user can retry
    }
  }, [
    order,
    canReject,
    isSubmitting,
    onReject,
    rejectComment,
    resetRejectState,
  ]);

  const enterRejectMode = useCallback(() => {
    if (!canReject || isSubmitting) return;
    setRejectMode(true);
    setRejectError("");
  }, [canReject, isSubmitting]);

  const cancelRejectMode = useCallback(() => {
    resetRejectState();
  }, [resetRejectState]);

  const setAttIdxStable = useCallback((idx: number) => setAttIdx(idx), []);

  return {
    attachments,
    currentAttachment,
    attIdx,
    setAttIdx: setAttIdxStable,
    goNextAttachment,
    goPrevAttachment,
    mediaLoading,
    setMediaLoading,

    canAccept,
    canReject,
    noActions,
    handleAccept,

    rejectMode,
    rejectComment,
    rejectError,
    setRejectComment,
    setRejectError,
    enterRejectMode,
    cancelRejectMode,
    handleRejectConfirm,
    textareaRef,
  };
};
